/**
 * Predictive-search analytics bridge.
 *
 * Fires a custom customer event once the shopper has stopped typing in the
 * search drawer, so GA4 can see what people search for in the predictive
 * panel. Shopify's standard `search_submitted` event only fires on the full
 * /search results page, so typeahead queries were invisible.
 *
 * Why a custom event and not a dataLayer push: GTM/GA4 live inside a Shopify
 * custom web pixel, which runs in a sandboxed iframe with its OWN window and
 * its OWN dataLayer. A dataLayer.push() from theme JS would write to a
 * different object and never reach GTM. `Shopify.analytics.publish()` is the
 * sanctioned bridge — the pixel subscribes to the same event name and the
 * payload arrives on `event.customData`.
 *
 * Consent is handled for free: the pixels are permission-gated on Marketing +
 * Analytics, so nothing is forwarded until the shopper accepts the banner.
 *
 * Pixel side (Admin -> Settings -> Customer events), see docs/pixels/:
 *   analytics.subscribe('mediashop:predictive_search', (event) => { ... });
 */
(function () {
  'use strict';

  // The snippet is rendered up to three times (desktop / minimal / mobile),
  // so this file can be included more than once. One delegated listener is
  // enough for every instance.
  if (window.msPredictiveSearchTracking) return;
  window.msPredictiveSearchTracking = true;

  var EVENT_NAME = 'mediashop:predictive_search';

  // Requested by the data team: only report once the shopper has paused.
  var IDLE_MS = 2500;
  // Below this, results are noise ("h", "ha") and would flood the report.
  var MIN_QUERY_LENGTH = 3;
  // How long to keep waiting for an in-flight query before giving up.
  var SETTLE_TIMEOUT_MS = 4000;
  var SETTLE_POLL_MS = 100;

  var INPUT_SELECTOR = 'input.input-search[name="q"]';
  // NOTE: #search-result is a duplicate id across the rendered instances, so
  // never use getElementById here — always scope to the owning component.
  var ROOT_SELECTOR = '.header-search';
  var RESULTS_SELECTOR = '.ms-search-drawer__results';

  var idleTimers = new WeakMap();
  var lastPublished = new WeakMap();

  function getRoot(input) {
    return input.closest(ROOT_SELECTOR);
  }

  /**
   * The Alpine component (`xModalSearch`) owns the real loading flag. Reading
   * it is more reliable than guessing from the DOM, but Alpine is loaded
   * deferred and lazily initialised, so treat its absence as "not loading"
   * rather than blocking forever.
   */
  function isLoading(root) {
    try {
      if (!window.Alpine || typeof window.Alpine.$data !== 'function') return false;
      var data = window.Alpine.$data(root);
      return !!(data && data.loading);
    } catch (e) {
      return false;
    }
  }

  /**
   * Result counts come from the merged markup the component renders into
   * `x-html`. `data-native-results-count` is only present on the branch that
   * renders the native search section, so fall back to counting the cards.
   */
  function readResults(root) {
    var container = root.querySelector(RESULTS_SELECTOR);
    if (!container) return null;

    // Zero-result recommendations (Globo "No results" products) are not hits.
    var shown = container.querySelectorAll('.ms-predictive__item:not(.ms-predictive__item--recommendation)').length;
    var panel = container.querySelector('.ms-predictive');
    var total = panel ? parseInt(panel.getAttribute('data-native-results-count'), 10) : NaN;

    return { shown: shown, total: isNaN(total) ? shown : total };
  }

  /**
   * Wait for the in-flight query to finish. A zero-result search is the most
   * interesting data point of all, so an empty panel still counts as settled —
   * we only wait on the loading flag.
   */
  function whenSettled(root, callback) {
    var waited = 0;

    (function poll() {
      if (!isLoading(root) || waited >= SETTLE_TIMEOUT_MS) {
        callback();
        return;
      }
      waited += SETTLE_POLL_MS;
      setTimeout(poll, SETTLE_POLL_MS);
    })();
  }

  function publish(input) {
    var root = getRoot(input);
    if (!root) return;

    var query = (input.value || '').trim();
    if (query.length < MIN_QUERY_LENGTH) return;
    // The component caches results and short-circuits before any fetch, so a
    // backspace-and-retype would otherwise re-report the same term.
    if (lastPublished.get(input) === query) return;

    whenSettled(root, function () {
      // The shopper resumed typing while we were waiting — the next idle
      // timeout will report the newer term instead.
      if ((input.value || '').trim() !== query) return;

      if (!window.Shopify || !window.Shopify.analytics || typeof window.Shopify.analytics.publish !== 'function') {
        return;
      }

      var counts = readResults(root);
      lastPublished.set(input, query);

      try {
        window.Shopify.analytics.publish(EVENT_NAME, {
          search_term: query,
          results_count: counts ? counts.total : 0,
          results_shown: counts ? counts.shown : 0,
          url: window.location.href,
        });
      } catch (e) {
        /* Analytics must never break the search UI. */
      }
    });
  }

  function scheduleFor(input) {
    clearTimeout(idleTimers.get(input));

    var query = (input.value || '').trim();
    if (query === '') {
      // Cleared the field: allow the same term to be reported again later.
      lastPublished.delete(input);
      return;
    }

    idleTimers.set(
      input,
      setTimeout(function () {
        publish(input);
      }, IDLE_MS)
    );
  }

  document.addEventListener('input', function (e) {
    var input = e.target.closest ? e.target.closest(INPUT_SELECTOR) : null;
    if (!input) return;
    scheduleFor(input);
  });

  // Submitting the drawer goes to /search, where Shopify's own
  // `search_submitted` event fires. Cancel ours so the term isn't counted twice.
  document.addEventListener(
    'submit',
    function (e) {
      if (!e.target.querySelectorAll) return;
      var inputs = e.target.querySelectorAll(INPUT_SELECTOR);
      for (var i = 0; i < inputs.length; i++) {
        clearTimeout(idleTimers.get(inputs[i]));
      }
    },
    true
  );
})();
